import { useState, type ReactNode } from 'react';
import { Bot, ChevronDown, Hourglass, User } from 'lucide-react';
import type { FilaDeCartera } from '@/api/cartera.api';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { enlaceTel, legible } from '@/lib/cartera';
import { cn } from '@/lib/cn';
import { PIPELINE_COLUMNS } from '@/lib/crm';
import { formatPhoneNumber } from '@/lib/format';
import { editorDe, type EditableLeadField } from '@/lib/lead-fields';
import { CERRADO, posicionDe } from '@/lib/proceso';
import { YA_CERRADO, claveDeAsignado, conMiles, gruposDeAsignado, gruposDeEtapa, proximaDe, type Asignables } from '@/lib/tablero-clientes';
import { EditorDeCampo } from './EditorDeCampo';

/**
 * LAS CELDAS DE LA TABLA DE «CLIENTES» (tanda 6). Cada una pinta un dato de la fila y, si se
 * puede cambiar, avisa con su `on…`: guardar (y volver atrás si falla) es de quien las usa
 * (`useEdicionDeClientes`). Casi todas sin estado: se prueban con renderToStaticMarkup.
 *
 * Los desplegables son `<select>` de verdad, invisibles, puestos ENCIMA de la píldora que se ve:
 * el teléfono abre su selector nativo, la lista nunca queda recortada por la caja de la tabla, y
 * lo que se toca mide 44 px aunque la píldora sea más baja. Lo que se lee sale de la fila, no del
 * `<select>`: por eso un cambio se ve en el acto, y uno cancelado no deja nada a medias.
 */

const APAGADO = 'text-sm text-slate-500 dark:text-slate-400';
const TOQUE = 'relative inline-flex min-h-[44px] w-full items-center md:min-h-0';
// `text-base`: con letra de menos de 16 px, iOS acerca la página al abrir el selector.
const ENCIMA = 'peer absolute inset-0 h-full w-full cursor-pointer appearance-none text-base opacity-0 disabled:cursor-default';
const FOCO = 'peer-focus-visible:ring-2 peer-focus-visible:ring-brand-500 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface';

interface Opcion {
  value: string;
  label: string;
}

/** Elegir en un `<select>` que no manda sobre lo que se ve: se devuelve a su valor y se avisa del nuevo. */
function alElegir(valor: string, onCambiar: (nuevo: string) => void) {
  return (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nuevo = e.target.value;
    // Si el cambio se cancela (o falla), el selector no se queda marcando lo que no es.
    e.target.value = valor;
    if (nuevo !== valor) onCambiar(nuevo);
  };
}

/** Una píldora de estado. Con `opciones` se puede cambiar; sin ellas, solo dice. */
function Pildora({
  nombre,
  etiqueta,
  tono,
  valor,
  opciones,
  onCambiar,
  ayuda,
}: {
  /** Para lectores de pantalla: «Etapa de Ana Ejemplo». */
  nombre: string;
  etiqueta: string;
  /** Clases de fondo y de letra. */
  tono: string;
  valor: string;
  opciones?: Opcion[];
  onCambiar?: (valor: string) => void;
  ayuda?: string;
}) {
  const cuerpo = cn('inline-flex h-7 w-full items-center justify-center gap-1 rounded-full px-2.5 text-xs font-semibold', tono);
  if (!opciones || !onCambiar) {
    return (
      <span title={ayuda} className={cn(cuerpo, 'max-w-[9.5rem]')}>
        <span className="truncate">{etiqueta}</span>
      </span>
    );
  }
  return (
    <span className={cn(TOQUE, 'max-w-[9.5rem]')}>
      <select aria-label={nombre} value={valor} onChange={alElegir(valor, onCambiar)} className={ENCIMA}>
        {opciones.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <span aria-hidden className={cn(cuerpo, FOCO, 'pointer-events-none transition-[filter] duration-150 peer-hover:brightness-110')}>
        <span className="truncate">{etiqueta}</span>
        <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-75" />
      </span>
    </span>
  );
}

/** Avatar con el punto de su temperatura y el nombre, que abre la ficha. */
export function CeldaDeCliente({ fila, onVerFicha }: { fila: FilaDeCartera; onVerFicha?: () => void }) {
  const temperatura = PIPELINE_COLUMNS.find((c) => c.status === fila.conversacion?.temperatura);
  const nombreDeTemperatura = temperatura?.label ?? 'Sin temperatura';
  const punto = (clase: string) => (
    <span title={nombreDeTemperatura} className={cn('h-2.5 w-2.5 rounded-full', temperatura?.dot ?? 'bg-slate-300 dark:bg-slate-600', clase)}>
      <span className="sr-only">{nombreDeTemperatura}</span>
    </span>
  );
  return (
    <span className="flex w-36 min-w-0 items-center gap-2 md:w-44 md:gap-2.5 2xl:w-56">
      {/* En el teléfono esta columna va fija y cada píxel es de la tabla: solo el punto. Con sitio, el avatar con su punto. */}
      {punto('shrink-0 md:hidden')}
      <span className="relative hidden shrink-0 md:block">
        <Avatar name={fila.nombre} src={fila.avatarUrl} size="sm" />
        {punto('absolute -bottom-0.5 -right-0.5 ring-2 ring-surface')}
      </span>
      <button
        type="button"
        onClick={onVerFicha}
        title={`Ver la ficha de ${fila.nombre}`}
        className={
          'min-h-[44px] min-w-0 truncate rounded text-left font-medium text-slate-900 transition-colors hover:text-brand-700 hover:underline ' +
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 md:min-h-0 dark:text-slate-100 dark:hover:text-brand-300'
        }
      >
        {fila.nombre}
      </button>
      {fila.personal && <Badge className="hidden shrink-0 md:inline-flex">Personal</Badge>}
    </span>
  );
}

/**
 * La etapa, en una píldora con el color de su grupo. Se cambia con su desplegable (también a
 * «Cerrado»); un cliente ya cerrado no lo trae: se reabre desde la ficha.
 */
export function CeldaDeEtapa({ fila, etapas, onCambiar }: { fila: FilaDeCartera; etapas: readonly string[]; onCambiar?: (etapa: string) => void }) {
  const grupos = gruposDeEtapa(etapas);
  const actual = grupos[posicionDe(fila, etapas)]!;
  const cerrado = actual.clave === CERRADO;
  return (
    <Pildora
      nombre={`Etapa de ${fila.nombre}`}
      etiqueta={actual.etiqueta}
      tono={`${actual.color} text-white`}
      valor={actual.clave}
      opciones={cerrado ? undefined : grupos.map((g) => ({ value: g.clave, label: g.etiqueta }))}
      onCambiar={onCambiar}
      ayuda={cerrado ? YA_CERRADO : undefined}
    />
  );
}

// Los mismos tonos que los puntos del pipeline (lib/crm), subidos lo justo para que se lea la letra encima.
const TONO_DE_TEMPERATURA: Record<string, string> = {
  CALIENTE: 'bg-red-600 text-white',
  TIBIO: 'bg-amber-400 text-amber-950',
  FRIO: 'bg-sky-700 text-white',
  LARGO_PLAZO: 'bg-violet-600 text-white',
  CERRADO: 'bg-emerald-700 text-white',
  NO_CERRADO: 'bg-slate-500 text-white',
};
const SIN_CLASIFICAR = 'border border-dashed border-slate-300 text-slate-600 dark:border-slate-600 dark:text-slate-300';

/**
 * La temperatura del chat. Sin conversación no hay temperatura: «Sin chat». Uno ya cerrado
 * no se reabre desde la tabla (un clic de más dispararía el aviso de «caliente»): va sin desplegable.
 */
export function CeldaDeTemperatura({
  fila,
  opciones,
  onCambiar,
}: {
  fila: FilaDeCartera;
  /** Las temperaturas con el nombre que les da el negocio (`leadStatusOptionsFrom`). */
  opciones: Opcion[];
  /** `null` = sin clasificar. */
  onCambiar?: (temperatura: string | null) => void;
}) {
  if (!fila.conversacion) return <span className={APAGADO}>Sin chat</span>;
  const actual = fila.conversacion.temperatura ?? '';
  const cerrado = actual === 'CERRADO';
  return (
    <Pildora
      nombre={`Temperatura de ${fila.nombre}`}
      etiqueta={actual ? (opciones.find((o) => o.value === actual)?.label ?? legible(actual.toLowerCase())) : 'Sin clasificar'}
      tono={TONO_DE_TEMPERATURA[actual] ?? SIN_CLASIFICAR}
      valor={actual}
      opciones={cerrado ? undefined : [{ value: '', label: 'Sin clasificar' }, ...opciones]}
      onCambiar={onCambiar && ((v) => onCambiar(v || null))}
      ayuda={cerrado ? YA_CERRADO : undefined}
    />
  );
}

/** Las opciones de «Asignar»: los agentes de IA, yo y el equipo. Van dentro de un `<select>`. */
export function OpcionesDeAsignar({ asignables: { agentes, personas } }: { asignables: Asignables }) {
  return (
    <>
      {agentes.length > 0 && (
        <optgroup label="Agente de IA">
          {agentes.map((a) => (
            <option key={a.id} value={`agente:${a.id}`}>
              {a.name}
            </option>
          ))}
        </optgroup>
      )}
      <option value="yo">Lo atiendo yo</option>
      {personas && personas.length > 0 && (
        <optgroup label="Una persona del equipo">
          {personas.map((p) => (
            <option key={p.id} value={`persona:${p.id}`}>
              {p.nombre}
            </option>
          ))}
        </optgroup>
      )}
    </>
  );
}

/** En manos de quién está su chat (la IA, yo, alguien del equipo), con el desplegable de «Asignar». */
export function CeldaDeAsignado({
  fila,
  asignables,
  ocupado = false,
  onAsignar,
}: {
  fila: FilaDeCartera;
  asignables: Asignables;
  ocupado?: boolean;
  /** `agente:<id>`, `yo` o `persona:<id>`. */
  onAsignar?: (valor: string) => void;
}) {
  const clave = claveDeAsignado(fila, asignables);
  if (clave === 'sin-chat') return <span className={APAGADO}>Sin chat</span>;
  const etiqueta = gruposDeAsignado(asignables).find((g) => g.clave === clave)?.etiqueta ?? 'La IA';
  const deLaIa = clave === 'ia' || clave.startsWith('agente:');
  const Icono = deLaIa ? Bot : clave === 'espera' ? Hourglass : User;
  // «La IA» a secas (el agente del canal), «alguien del equipo» y «en espera» no se pueden elegir: solo se muestran.
  const seElige = clave === 'yo' || clave.startsWith('agente:') || clave.startsWith('persona:');
  return (
    <span className={cn(TOQUE, 'max-w-[11rem]')}>
      {onAsignar && (
        <select aria-label={`Asignar el chat de ${fila.nombre}`} value={clave} disabled={ocupado} onChange={alElegir(clave, onAsignar)} className={ENCIMA}>
          {!seElige && (
            <option value={clave} disabled hidden>
              {etiqueta}
            </option>
          )}
          <OpcionesDeAsignar asignables={asignables} />
        </select>
      )}
      <span
        aria-hidden={onAsignar ? true : undefined}
        className={cn(
          'pointer-events-none flex h-8 w-full min-w-0 items-center gap-2 rounded-md px-1.5 text-sm text-slate-700 transition-colors peer-hover:bg-slate-100 dark:text-slate-200 dark:peer-hover:bg-slate-800',
          FOCO,
        )}
      >
        <span
          className={cn(
            'grid h-6 w-6 shrink-0 place-items-center rounded-full',
            deLaIa
              ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/50 dark:text-brand-200'
              : clave === 'espera'
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
          )}
        >
          <Icono className="h-3.5 w-3.5" />
        </span>
        <span className="truncate">{etiqueta}</span>
        {onAsignar && <ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 text-slate-400 opacity-0 transition-opacity peer-hover:opacity-100" />}
      </span>
    </span>
  );
}

const TONO_DE_PLAZO = {
  normal: 'text-slate-500 dark:text-slate-400',
  alerta: 'font-medium text-amber-700 dark:text-amber-400',
  apagado: 'text-slate-500 dark:text-slate-400',
} as const;

/** Qué toca hacer con él y para cuándo; vencido, en tono de alerta. Solo lectura. */
export function CeldaDeProxima({ fila, ahora }: { fila: FilaDeCartera; ahora: Date }) {
  const { que, cuando, tono } = proximaDe(fila, ahora);
  // Con dos líneas, arriba va lo que hay que hacer (en firme) y abajo el cuándo (con su tono).
  const arriba = cuando || tono === 'normal' ? 'text-slate-900 dark:text-slate-100' : TONO_DE_PLAZO[tono];
  return (
    <div data-tono={tono} className="w-40 leading-snug 2xl:w-56">
      <p title={que} className={cn('truncate', arriba)}>
        {que}
      </p>
      {cuando && <p className={cn('truncate text-xs', TONO_DE_PLAZO[tono])}>{cuando}</p>}
    </div>
  );
}

/** El teléfono con formato, como enlace para llamar. */
export function CeldaDeTelefono({ fila }: { fila: FilaDeCartera }) {
  const tel = enlaceTel(fila.telefono);
  const texto = formatPhoneNumber(fila.telefono);
  if (!texto) return <span className={APAGADO}>—</span>;
  return tel ? (
    <a href={tel} className="inline-flex min-h-[44px] items-center whitespace-nowrap text-brand-700 hover:underline md:min-h-0 dark:text-brand-300">
      {texto}
    </a>
  ) : (
    <span className="whitespace-nowrap">{texto}</span>
  );
}

const CELDA = 'min-h-[44px] select-text md:min-h-0';

/**
 * Un campo de la ficha en su celda, con el editor de su tipo. Un número se lee con separadores
 * de miles («400,000») y al tocarlo se vuelve un campo numérico; al salir, vuelve a leerse.
 */
export function CeldaDeFicha({
  campo,
  valor,
  onGuardar,
}: {
  campo: Pick<EditableLeadField, 'key' | 'label' | 'tipo' | 'opciones'>;
  valor: string;
  onGuardar: (valor: string) => void | Promise<unknown>;
}) {
  const [editando, setEditando] = useState(false);
  const numero = editorDe(campo, valor) === 'numero';
  if (numero && !editando) {
    return (
      <button
        type="button"
        aria-label={`${campo.label}: ${valor ? conMiles(valor) : 'sin dato'}. Editar`}
        onClick={() => setEditando(true)}
        onFocus={() => setEditando(true)}
        className={cn(
          CELDA,
          'h-8 w-full rounded-md border border-transparent px-1.5 text-right tabular-nums transition-colors hover:border-surface-border dark:hover:border-slate-700',
        )}
      >
        {valor ? conMiles(valor) : <span className="text-slate-500">—</span>}
      </button>
    );
  }
  return (
    // El `blur` del campo sube hasta aquí: primero guarda él y después la celda vuelve a solo leerse.
    <span className="block" onBlur={numero ? () => setEditando(false) : undefined}>
      <EditorDeCampo campo={campo} valor={valor} onGuardar={onGuardar} compacto enfocado={numero} className={cn(CELDA, numero && 'text-right tabular-nums')} />
    </span>
  );
}

/** Un desplegable del lote («Cambiar etapa…», «Asignar a…»): al elegir, actúa sobre las marcadas y vuelve a su rótulo. */
export function DesplegableDeLote({ rotulo, ocupado = false, onElegir, children }: { rotulo: string; ocupado?: boolean; onElegir: (valor: string) => void; children: ReactNode }) {
  return (
    <select
      aria-label={rotulo}
      value=""
      disabled={ocupado}
      onChange={alElegir('', onElegir)}
      className={
        'h-8 min-h-[44px] max-w-[11rem] rounded-md border border-surface-border bg-surface px-2 text-base font-medium text-slate-700 ' +
        'focus:border-brand-500 focus:outline-none disabled:opacity-60 md:min-h-0 md:text-xs dark:border-slate-700 dark:text-slate-200'
      }
    >
      <option value="" disabled hidden>
        {rotulo}
      </option>
      {children}
    </select>
  );
}
